import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useCreateOrganization } from "@/hooks/useOrganizations";

interface CreateOrgDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Create a new organization. The backend also creates a root group with the same name.
 * Users (and the first Client Admin) are added later via a proper onboarding flow.
 */
export default function CreateOrgDialog({ open, onClose }: CreateOrgDialogProps) {
  const createOrg = useCreateOrganization();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setName("");
      setError(null);
    }
  }, [open]);

  const canSubmit = name.trim().length > 0 && !createOrg.isPending;

  function handleSubmit() {
    setError(null);
    createOrg.mutate(name.trim(), {
      onSuccess: () => onClose(),
      onError: (e) => setError(e instanceof Error ? e.message : "Failed to create organization."),
    });
  }

  return (
    <Dialog open={open} onClose={createOrg.isPending ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ fontFamily: "var(--font-crimson)", fontSize: 24 }}>
        New organization
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
          <TextField
            label="Organization name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <Typography sx={{ fontSize: 12.5, color: "var(--color-graphite)" }}>
            A root group with the same name is created automatically. Add users afterward.
          </Typography>
          {error && <Typography sx={{ fontSize: 13, color: "error.main" }}>{error}</Typography>}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          disabled={createOrg.isPending}
          sx={{ color: "var(--color-graphite)" }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!canSubmit}
          sx={{ bgcolor: "var(--color-rodeo-black)" }}
        >
          {createOrg.isPending ? "Creating…" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
